import { Constants } from '@pwc/pwc-deals-web-common';
import _get from 'lodash/get';
import _flatMap from 'lodash/flatMap';
import _map from 'lodash/fp/map';
import _isEmpty from 'lodash/isEmpty';
import _isFinite from 'lodash/isFinite';
import _isUndefined from 'lodash/isUndefined';
import _keys from 'lodash/keys';
import _pickBy from 'lodash/pickBy';
import _values from 'lodash/values';
import _pipe from 'lodash/fp/pipe';
import { createSelector } from 'reselect';
import { linkedAdjustmentKeyBuilder } from '../utilities/linkedAdjustmentsUtilities';
import { sanitizeSummableValue } from '../utilities/structured-data-utilities';
import {
  adjustmentTypeEbitdaContributionName,
  adjustmentTypeWorkingCapitalContributionName,
} from '../../services/textService';
import { getIsViewingToggled } from '../reducers/calcEngine';
import {
  adjustmentTypesNotParticipatingInCalculations,
  adjustmentTypesParticipatingInCalculations,
} from '../utilities/adjustmentUtilities';
import {
  ADJUSTMENT_SUMS_BY_CODE_KEY,
  ADJUSTMENT_TYPES_BY_CODE_KEY,
  ALL_ADJUSTMENTS_KEY,
  REPORTED_DEBT_KEY,
  DEBT_LIKE_ITEMS_KEY,
  LABEL_KEY,
  REPORTED_CASH_KEY,
  LONG_DESCRIPTIONS_BY_CODE_KEY,
  REVENUE_ADJUSTMENTS_KEY,
  TOGGLED_ADJUSTMENT_SUMS_BY_CODE_KEY,
  TOTALS_KEY,
} from '../utilities/constants';
import {
  getReportedEbitda,
  getReportedWorkingCapital,
} from './factCalculationsSelectors';
import {
  getActiveChartTimePeriodsForNwc,
  getTimePeriodsForDdl,
  getTimePeriodsForNwC,
  getTimePeriodsForQofE,
} from './timePeriodSelectors';

export const getAdjustmentLongDescriptionsByCode = (state) =>
  state[LONG_DESCRIPTIONS_BY_CODE_KEY];

// Never use this directly, use getAdjustmentSumsByCode() instead
const getToggledAdjustmentSumsByCode = (state) =>
  state[TOGGLED_ADJUSTMENT_SUMS_BY_CODE_KEY];

// Never use this directly, use getAdjustmentSumsByCode() instead
const getPlainAdjustmentSumsByCode = (state) =>
  state[ADJUSTMENT_SUMS_BY_CODE_KEY];

export const getAdjustmentSumsByCode = createSelector(
  getPlainAdjustmentSumsByCode,
  getToggledAdjustmentSumsByCode,
  getIsViewingToggled,
  (plainSums, toggledSums, isViewingToggled) =>
    isViewingToggled ? toggledSums : plainSums
);

export const getAdjustmentTypesByCode = (state) =>
  state[ADJUSTMENT_TYPES_BY_CODE_KEY];

export const createSelectorGetRevenueAdjustmentsForDocumentCode = (
  documentCode
) =>
  createSelector(getAdjustmentSumsByCode, (adjustmentSumsByCode) =>
    _get(
      adjustmentSumsByCode,
      `${documentCode}.${REVENUE_ADJUSTMENTS_KEY}.${TOTALS_KEY}`,
      {}
    )
  );

export const createSelectorHasCalculationsForDocumentCode = (documentCode) =>
  createSelector(
    getAdjustmentSumsByCode,
    (adjustmentSumsByCode) =>
      !_isUndefined(_get(adjustmentSumsByCode, documentCode, undefined))
  );

export const createSelectorGetAdjustmentSumsForDocumentCode = (documentCode) =>
  createSelector(getAdjustmentSumsByCode, (adjustmentSumsByCode) =>
    _get(adjustmentSumsByCode, documentCode, {})
  );

export const createSelectorGetAdjustmentSumsParticipatingInCalculationsForDocumentCode =
  (documentCode) =>
    createSelector(getAdjustmentSumsByCode, (adjustmentSumsByCode) => {
      const adjustmentSumsForCode = _get(
        adjustmentSumsByCode,
        `${documentCode}.${ALL_ADJUSTMENTS_KEY}`,
        {}
      );
      const nonTotalsKeys = _pickBy(
        adjustmentSumsForCode,
        (_, key) => key !== TOTALS_KEY
      );
      const calcParticipatingAdjustmentSumsForCode =
        adjustmentTypesParticipatingInCalculations(nonTotalsKeys);
      return calcParticipatingAdjustmentSumsForCode;
    });

export const createSelectorGetAdjustmentSumsNotParticipatingInCalculationsForDocumentCode =
  (documentCode) =>
    createSelector(getAdjustmentSumsByCode, (adjustmentSumsByCode) => {
      const adjustmentSumsForCode = _get(
        adjustmentSumsByCode,
        `${documentCode}.${ALL_ADJUSTMENTS_KEY}`,
        {}
      );
      const nonTotalsKeys = _pickBy(
        adjustmentSumsForCode,
        (_, key) => key !== TOTALS_KEY
      );
      const calcNonParticipatingAdjustmentSumsForCode =
        adjustmentTypesNotParticipatingInCalculations(nonTotalsKeys);
      return calcNonParticipatingAdjustmentSumsForCode;
    });

const createSelectorBuildAdjustmentOptions = (documentCode) =>
  createSelector(getAdjustmentTypesByCode, (adjustmentTypesByCode) => {
    const adjustmentTypesForDocument = _get(
      adjustmentTypesByCode,
      documentCode,
      {}
    );

    const options = [];

    _keys(adjustmentTypesForDocument).forEach((groupName) =>
      _keys(adjustmentTypesForDocument[groupName]).forEach((key) => {
        const spaceIndex = key.indexOf(' ');
        const adjustmentNumber = key.substring(0, spaceIndex).trim();
        const adjustmentName = key.substring(spaceIndex).trim();

        options.push({
          value: linkedAdjustmentKeyBuilder(
            documentCode,
            groupName,
            adjustmentNumber,
            adjustmentName
          ),
          label: `${adjustmentNumber} ${adjustmentName}`,
        });
      })
    );

    return options;
  });

export const getAdjustmentSumsParticipatingInCalculationsForQofE =
  createSelectorGetAdjustmentSumsParticipatingInCalculationsForDocumentCode(
    Constants.QUALITY_OF_EARNINGS
  );

export const getAdjustmentSumsNotParticipatingInCalculationsForQofE =
  createSelectorGetAdjustmentSumsNotParticipatingInCalculationsForDocumentCode(
    Constants.QUALITY_OF_EARNINGS
  );

export const getRevenueAdjustmentsForQofE =
  createSelectorGetRevenueAdjustmentsForDocumentCode(
    Constants.QUALITY_OF_EARNINGS
  );

export const getAdjustmentOptionsForQofE = createSelectorBuildAdjustmentOptions(
  Constants.QUALITY_OF_EARNINGS
);

export const getAdjustmentSumsForNwc =
  createSelectorGetAdjustmentSumsForDocumentCode(Constants.NET_WORKING_CAPITAL);

export const getAdjustmentOptionsForNwc = createSelectorBuildAdjustmentOptions(
  Constants.NET_WORKING_CAPITAL
);

export const getAdjustmentOptionsForDdl = createSelectorBuildAdjustmentOptions(
  Constants.DEBT_AND_DEBT_LIKE
);

const reduceAdjustmentTypeContributionsWithRunningTotals = (
  adjustmentSums,
  runningTotals,
  getLabelFn
) => {
  const adjustmentTypeContributions = _keys(adjustmentSums).reduce(
    (contributions, typeKey) => {
      const totalsByPeriod = adjustmentSums[typeKey][TOTALS_KEY];

      const currentTypeTotals = _keys(totalsByPeriod).reduce(
        (typeTotals, period) => {
          if (runningTotals[period] !== undefined) {
            runningTotals[period] += sanitizeSummableValue(
              totalsByPeriod[period]
            );
            typeTotals[period] = runningTotals[period];
          }
          return typeTotals;
        },
        {}
      );

      contributions[typeKey] = {
        [LABEL_KEY]: getLabelFn(typeKey),
        [TOTALS_KEY]: currentTypeTotals,
      };
      return contributions;
    },
    {}
  );

  return adjustmentTypeContributions;
};

export const getAdjustmentTypesEbitdaContributionForQofE = createSelector(
  getTimePeriodsForQofE,
  getAdjustmentSumsParticipatingInCalculationsForQofE,
  getReportedEbitda,
  (qofeTimePeriods, adjustmentSums, reportedEbitda) => {
    const runningTotals = qofeTimePeriods.reduce(
      (totals, period) => ({ ...totals, [period]: reportedEbitda[period] }),
      {}
    );

    const ebitdaContributions =
      reduceAdjustmentTypeContributionsWithRunningTotals(
        adjustmentSums,
        runningTotals,
        adjustmentTypeEbitdaContributionName
      );
    return ebitdaContributions;
  }
);

export const getAdjustmentSumsParticipatingInCalculationsForNwc =
  createSelectorGetAdjustmentSumsParticipatingInCalculationsForDocumentCode(
    Constants.NET_WORKING_CAPITAL
  );

export const getAdjustmentSumsNotParticipatingInCalculationsForNwc =
  createSelectorGetAdjustmentSumsNotParticipatingInCalculationsForDocumentCode(
    Constants.NET_WORKING_CAPITAL
  );

export const getAdjustmentSumsNotParticipatingInCalculationsForDdl =
  createSelectorGetAdjustmentSumsNotParticipatingInCalculationsForDocumentCode(
    Constants.DEBT_AND_DEBT_LIKE
  );

export const getAdjustmentTypesWorkingCapitalContributionForNwc =
  createSelector(
    getTimePeriodsForNwC,
    getAdjustmentSumsParticipatingInCalculationsForNwc,
    getReportedWorkingCapital,
    (nwcTimePeriods, adjustmentSums, reportedWorkingCapital) => {
      const runningTotals = nwcTimePeriods.reduce(
        (totals, period) => ({
          ...totals,
          [period]: reportedWorkingCapital[period],
        }),
        {}
      );

      const capitalContributions =
        reduceAdjustmentTypeContributionsWithRunningTotals(
          adjustmentSums,
          runningTotals,
          adjustmentTypeWorkingCapitalContributionName
        );
      return capitalContributions;
    }
  );

export const getAdjustedWorkingCapitalForNwc = createSelector(
  getTimePeriodsForNwC,
  getAdjustmentSumsForNwc,
  getReportedWorkingCapital,
  (timePeriods, adjustmentSums, workingCapital) => {
    return timePeriods.reduce((acc, currentTimePeriod) => {
      const adjTotalForPeriod = _get(
        adjustmentSums,
        [ALL_ADJUSTMENTS_KEY, TOTALS_KEY, currentTimePeriod],
        {}
      );
      const workingCapitalForPeriod = workingCapital[currentTimePeriod];
      const adjustedWorkingCapital =
        parseFloat(sanitizeSummableValue(adjTotalForPeriod)) +
        parseFloat(workingCapitalForPeriod);
      const sanitizedAdjustedWorkingCapital = _isFinite(adjustedWorkingCapital)
        ? adjustedWorkingCapital
        : undefined;
      return { ...acc, [currentTimePeriod]: sanitizedAdjustedWorkingCapital };
    }, {});
  }
);

export const getChartTimePeriodsAdjustedWorkingCapitalForNwc = createSelector(
  getActiveChartTimePeriodsForNwc,
  getAdjustedWorkingCapitalForNwc,
  (timePeriods, adjustedWorkingCapital) =>
    timePeriods.reduce(
      (chartPeriodsAdjustedWorkingCapital, timePeriod) => ({
        ...chartPeriodsAdjustedWorkingCapital,
        [timePeriod]: adjustedWorkingCapital[timePeriod],
      }),
      {}
    )
);

export const getAdjustmentSumsForDdl =
  createSelectorGetAdjustmentSumsForDocumentCode(Constants.DEBT_AND_DEBT_LIKE);

const createTotalsPlaceholderForAdjustmentType = (targetType) => ({
  [targetType]: { [TOTALS_KEY]: {} },
});

const pickAdjustmentType = (adjustmentTypes, targetType) => {
  const targetAdjustmentType = _pickBy(
    adjustmentTypes,
    (_, key) => key === targetType
  );

  return _isEmpty(targetAdjustmentType)
    ? createTotalsPlaceholderForAdjustmentType(targetType)
    : targetAdjustmentType;
};

const getAdjustmentTotalsForAdjustmentType = (adjustmentSums, targetType) => {
  const allAdjustments = _get(adjustmentSums, [ALL_ADJUSTMENTS_KEY], {});

  const adjustmentTypes = _pickBy(
    allAdjustments,
    (_, key) => key !== TOTALS_KEY
  );

  const targetAdjustmentType = pickAdjustmentType(adjustmentTypes, targetType);
  return targetAdjustmentType;
};

export const createSelectorGetFlattenAdjustmentObjectsByCodes = (
  documentCodes = []
) =>
  createSelector(getAdjustmentTypesByCode, (adjustmentTypesByCode) => {
    let flattenedAdjustments = [];

    documentCodes.forEach((documentCode) => {
      const adjustmentTypesForDocument = _get(
        adjustmentTypesByCode,
        documentCode,
        {}
      );
      const flatAdjustments = _pipe(
        _values,
        _map(_keys),
        _flatMap
      )(adjustmentTypesForDocument);

      const options = flatAdjustments.map((key) => {
        const spaceIndex = key.indexOf(' ');
        const adjustmentNumber = key.substring(0, spaceIndex).trim();
        const adjustmentName = key.substring(spaceIndex).trim();

        return {
          documentCode,
          adjustmentNumber,
          adjustmentName,
        };
      });

      flattenedAdjustments = flattenedAdjustments.concat(options);
    });

    return flattenedAdjustments;
  });

export const getFlattenAdjustmentObjectsForDocuments =
  createSelectorGetFlattenAdjustmentObjectsByCodes([
    Constants.QUALITY_OF_EARNINGS,
    Constants.NET_WORKING_CAPITAL,
    Constants.DEBT_AND_DEBT_LIKE,
  ]);

export const getBankDebtAdjustmentSumsForDdl = createSelector(
  getAdjustmentSumsForDdl,
  (adjustmentSums) =>
    getAdjustmentTotalsForAdjustmentType(adjustmentSums, REPORTED_DEBT_KEY)
);

export const getLessCashAdjustmentSumsForDdl = createSelector(
  getAdjustmentSumsForDdl,
  (adjustmentSums) =>
    getAdjustmentTotalsForAdjustmentType(adjustmentSums, REPORTED_CASH_KEY)
);

export const getDebtLikeItemAdjustmentSumsForDdl = createSelector(
  getAdjustmentSumsForDdl,
  (adjustmentSums) =>
    getAdjustmentTotalsForAdjustmentType(adjustmentSums, DEBT_LIKE_ITEMS_KEY)
);

export const getNetDebtForDdl = createSelector(
  getTimePeriodsForDdl,
  getBankDebtAdjustmentSumsForDdl,
  getLessCashAdjustmentSumsForDdl,
  (timePeriods, bankDebt, lessCash) =>
    timePeriods.reduce((acc, timePeriod) => {
      const bankDebtForPeriod = _get(
        bankDebt,
        [REPORTED_DEBT_KEY, TOTALS_KEY, timePeriod],
        undefined
      );
      const lessCashForPeriod = _get(
        lessCash,
        [REPORTED_CASH_KEY, TOTALS_KEY, timePeriod],
        undefined
      );

      const netForPeriod =
        parseFloat(sanitizeSummableValue(bankDebtForPeriod)) +
        parseFloat(sanitizeSummableValue(lessCashForPeriod));

      return {
        ...acc,
        [timePeriod]: netForPeriod,
      };
    }, {})
);

export const getNetDebtAndDebtLikeForDdl = createSelector(
  getTimePeriodsForDdl,
  getNetDebtForDdl,
  getDebtLikeItemAdjustmentSumsForDdl,
  (timePeriods, netDebt, debtLikeItems) =>
    timePeriods.reduce((acc, timePeriod) => {
      const netDebtForPeriod = _get(netDebt, [timePeriod], undefined);
      const debtLikeItemsForPeriod = _get(
        debtLikeItems,
        [DEBT_LIKE_ITEMS_KEY, TOTALS_KEY, timePeriod],
        undefined
      );

      const netForPeriod =
        netDebtForPeriod +
        parseFloat(sanitizeSummableValue(debtLikeItemsForPeriod));

      return {
        ...acc,
        [timePeriod]: netForPeriod,
      };
    }, {})
);

const prepAdjustmentSumsForTable = (adjustmentSums) => {
  const adjustmentSumsKeys = _keys(adjustmentSums);

  const tableReadySums = adjustmentSumsKeys.map((adjustmentType, index) => ({
    adjustmentType,
    adjustments: adjustmentSums[adjustmentType],
    isLastGroup: index === adjustmentSumsKeys.length - 1,
  }));

  return tableReadySums;
};

export const getTableReadyAdjustmentSumsParticipatingInCalculationsForQofE =
  createSelector(
    getAdjustmentSumsParticipatingInCalculationsForQofE,
    prepAdjustmentSumsForTable
  );

export const getTableReadyAdjustmentSumsNotParticipatingInCalculationsForQofE =
  createSelector(
    getAdjustmentSumsNotParticipatingInCalculationsForQofE,
    prepAdjustmentSumsForTable
  );

export const getTableReadyAdjustmentSumsParticipatingInCalculationsForNwc =
  createSelector(
    getAdjustmentSumsParticipatingInCalculationsForNwc,
    prepAdjustmentSumsForTable
  );

export const getTableReadyAdjustmentSumsNotParticipatingInCalculationsForNwc =
  createSelector(
    getAdjustmentSumsNotParticipatingInCalculationsForNwc,
    prepAdjustmentSumsForTable
  );

export const getTableReadyBankDebtAdjustmentSumsForDdl = createSelector(
  getBankDebtAdjustmentSumsForDdl,
  prepAdjustmentSumsForTable
);

export const getTableReadyLessCashAdjustmentSumsForDdl = createSelector(
  getLessCashAdjustmentSumsForDdl,
  prepAdjustmentSumsForTable
);

export const getTableReadyDebtLikeItemAdjustmentSumsForDdl = createSelector(
  getDebtLikeItemAdjustmentSumsForDdl,
  prepAdjustmentSumsForTable
);

export const getTableReadyAdjustmentSumsNotParticipatingInCalculationsForDdl =
  createSelector(
    getAdjustmentSumsNotParticipatingInCalculationsForDdl,
    prepAdjustmentSumsForTable
  );
